import { SurveyValidator, ValidatorResult } from "survey-core";

/**
 * Single source of truth for postal-code validation rules per country.
 * `regex` validates the normalised code (trimmed, upper-cased, whitespace
 * stripped); `example` feeds the default error message.
 */
interface PostalCodeRule {
  regex: RegExp;
  example: string;
}

const POSTAL_CODE_RULES: Record<string, PostalCodeRule> = {
  // Dutch postal code: four digits not starting with 0, followed by two
  // letters, with optional whitespace in between (e.g. "1234 AB" or "1234AB").
  NL: {
    regex: /^[1-9][0-9]{3}\s?[A-Za-z]{2}$/,
    example: "1234 AB",
  },
};

const getPostalCodeRule = (country: string): PostalCodeRule | undefined =>
  POSTAL_CODE_RULES[country.toUpperCase()];

const normalisePostalCode = (value: string): string =>
  value.trim().toUpperCase().replace(/\s+/g, "");

/**
 * Validates a postal code against a country-specific rule, so schemas only
 * need `{ "type": "postalcode", "country": "NL" }` instead of duplicating the
 * regex. Same contract as the shared package's validator; duplicated rather
 * than imported because importing from `@rumbledirect/survey` would pull its
 * antd-based question barrel into admin-v2's dependency graph.
 */
export class PostalCodeValidator extends SurveyValidator {
  getType(): string {
    return "postalcodevalidator";
  }

  get country(): string {
    return this.getPropertyValue("country");
  }
  set country(value: string) {
    this.setPropertyValue("country", value);
  }

  // The base type declares `ValidatorResult`, but survey-core's runtime
  // contract (and its own built-in validators) return `null` for "valid" —
  // the declaration is simply narrower than the implementation.
  validate(value: unknown, name?: string): ValidatorResult {
    // Let required-validation handle empty values; only validate the format.
    if (value === undefined || value === null || value === "") {
      return null as unknown as ValidatorResult;
    }

    const rule = getPostalCodeRule(this.country);
    // Unknown country: no rule to enforce, so do not block the respondent.
    if (!rule) {
      return null as unknown as ValidatorResult;
    }

    if (rule.regex.test(normalisePostalCode(String(value)))) {
      return null as unknown as ValidatorResult;
    }

    return new ValidatorResult(value, this.createCustomError(name ?? ""));
  }

  protected getDefaultErrorText(_name: string): string {
    const example = getPostalCodeRule(this.country)?.example ?? "";
    return `Voer een geldige postcode in (bijv. ${example})`;
  }
}
